import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MountpointConnectionService, MountPointConnection, PagedResult } from '../../services/mountpoint-connection.service';
import { UserService } from '../../services/user.service';
import { PermissionService } from '../../services/permission.service';
import { AcknowledgeDialogComponent } from '../acknowledge-dialog/acknowledge-dialog.component';
import { KeyValue } from '@angular/common';

interface AnalysisInputs {
  durationSec: number;
}

@Component({
  selector: 'app-rtk-base-analyzer-pro',
  templateUrl: './rtk-base-analyzer-pro.component.html',
  styleUrls: ['./rtk-base-analyzer-pro.component.scss']
})
export class RtkBaseAnalyzerProComponent implements OnInit, OnDestroy {
  title = 'RTK Base Analyzer (Pro Edition)';
  search = '';
  isLoading = false;
  errorMessage = '';

  connections: MountPointConnection[] = [];
  sortedConnections: MountPointConnection[] = [];

  currentPage = 1;
  readonly pageSize = 1;

  selectedConnection?: MountPointConnection;
  isDemo = false;

  readonly durationPresetOptions: { minutes: number; label: string }[] = Array
    .from({ length: 10 }, (_, idx) => {
      const minutes = idx + 1;
      return {
        minutes,
        label: minutes === 1 ? '1 minute' : `${minutes} minutes`
      };
    });

  private analysisInputsByKey: { [key: string]: AnalysisInputs } = {};
  analysisStatusByMountpoint: { [mp: string]: string } = {};
  analysisLoadingByMountpoint: { [mp: string]: boolean } = {};
  analysisFetchingByMountpoint: { [mp: string]: boolean } = {};
  analysisResultsByMountpoint: { [mp: string]: any } = {};
  analysisExpandedByMountpoint: { [mp: string]: boolean } = {};
  analysisNodeExpanded: { [mp: string]: { [path: string]: boolean } } = {};
  analyzerSummaryByMountpoint: { [mp: string]: any } = {};
  generatedAtByMountpoint: { [mp: string]: string } = {};
  snrCollapsedByMountpoint: { [mp: string]: boolean } = {};
  mapCollapsedByMountpoint: { [mp: string]: boolean } = {};
  coordsByMountpoint: { [mp: string]: { lat: number; lng: number } } = {};
  mapInstances: { [mp: string]: any } = {};
  showSnr = true;
  showMap = true;
  readonly signalQualityCategories: string[] = [
    'Very Poor',
    'Poor',
    'Fair',
    'Good',
    'Very Good',
    'Excellent'
  ];

  constructor(
    private readonly connectionsService: MountpointConnectionService,
    private readonly userService: UserService,
    private readonly snackBar: MatSnackBar,
    private readonly dialog: MatDialog,
    private readonly perms: PermissionService
  ) {
    this.perms.ensureFresh();
    this.isDemo = this.perms.has('demo');
  }

  ngOnInit(): void {
    this.loadConnections();
  }

  ngOnDestroy(): void {
    Object.keys(this.mapInstances).forEach(mp => {
      try {
        const inst = this.mapInstances[mp];
        if (inst && typeof inst.remove === 'function') {
          inst.remove();
        }
      } catch {
        // ignore cleanup errors
      }
    });
    this.mapInstances = {};
  }

  loadConnections(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.connections = [];
    this.sortedConnections = [];
    this.analysisInputsByKey = {};
    this.analysisStatusByMountpoint = {};
    this.analysisLoadingByMountpoint = {};
    this.analysisFetchingByMountpoint = {};
    this.analysisResultsByMountpoint = {};
    const pageSize = 100;
    this.fetchPage(0, pageSize, [], (all) => {
      this.connections = all;
      this.sortedConnections = [...this.connections].sort((a, b) => (a.mountPoint || '').localeCompare(b.mountPoint || ''));
      this.currentPage = 1;
      this.selectedConnection = this.currentConnection;
      if (this.selectedConnection) {
        this.ensureAnalysisInputsFor(this.selectedConnection);
        this.refreshAnalysisResult(this.selectedConnection);
        this.renderSummaryIfPresent(this.selectedConnection);
      }
      this.isLoading = false;
    });
  }

  private fetchPage(page: number, size: number, acc: MountPointConnection[], done: (all: MountPointConnection[]) => void): void {
    this.connectionsService.list(this.search, page, size).subscribe({
      next: (res: PagedResult<MountPointConnection>) => {
        const nextAcc = acc.concat(res.content || []);
        const totalPages = res.totalPages ?? 0;
        if (page + 1 < totalPages) {
          this.fetchPage(page + 1, size, nextAcc, done);
        } else {
          done(nextAcc);
        }
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'Failed to load mount point connections.';
        this.snackBar.open('Failed to load mount point connections.', 'Close', { duration: 4000 });
      }
    });
  }

  onSearch(): void {
    this.loadConnections();
  }

  get hasConnections(): boolean {
    return this.sortedConnections.length > 0;
  }

  get totalPages(): number {
    if (!this.hasConnections) return 0;
    return Math.ceil(this.sortedConnections.length / this.pageSize);
  }

  get currentConnection(): MountPointConnection | undefined {
    if (!this.hasConnections) return undefined;
    const index = (this.currentPage - 1) * this.pageSize;
    return this.sortedConnections[index];
  }

  selectFromDropdown(connectionKey: string): void {
    if (!connectionKey) return;
    const idx = this.sortedConnections.findIndex(c => this.getConnectionKey(c) === connectionKey);
    if (idx >= 0) {
      const page = Math.floor(idx / this.pageSize) + 1;
      this.goToPage(page);
    }
  }

  goFirst(): void { this.goToPage(1); }
  goLast(): void { if (this.totalPages > 0) { this.goToPage(this.totalPages); } }

  goPrev(): void {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  goNext(): void {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  goToPage(page: number): void {
    if (this.totalPages === 0) {
      this.currentPage = 1;
      this.selectedConnection = undefined;
      return;
    }
    const clamped = Math.min(Math.max(1, page), this.totalPages);
    this.currentPage = clamped;
    this.selectedConnection = this.currentConnection;
    if (this.selectedConnection) {
      this.ensureAnalysisInputsFor(this.selectedConnection);
      this.refreshAnalysisResult(this.selectedConnection);
      this.renderSummaryIfPresent(this.selectedConnection);
    }
  }

  get pageWindow(): number[] {
    const total = this.totalPages;
    if (total <= 0) return [];
    const targetCount = Math.min(5, total);
    const windowRadius = Math.floor(targetCount / 2);
    let start = Math.max(1, this.currentPage - windowRadius);
    let end = Math.min(total, start + targetCount - 1);
    start = Math.max(1, Math.min(start, end - targetCount + 1));
    const pages: number[] = [];
    for (let p = start; p <= end; p++) pages.push(p);
    return pages;
  }

  goToConnection(conn: MountPointConnection): void {
    if (!conn) return;
    const idx = this.sortedConnections.findIndex(c => this.getConnectionKey(c) === this.getConnectionKey(conn));
    if (idx >= 0) {
      const page = Math.floor(idx / this.pageSize) + 1;
      this.goToPage(page);
    }
  }

  // ---------- Analysis helpers ----------

  ensureAnalysisInputsFor(conn: MountPointConnection): void {
    const key = this.getConnectionKey(conn);
    if (!this.analysisInputsByKey[key]) {
      this.analysisInputsByKey[key] = { durationSec: 5 * 60 };
    }
  }

  getAnalysisDurationSelection(conn: MountPointConnection): number {
    const key = this.getConnectionKey(conn);
    this.ensureAnalysisInputsFor(conn);
    const mins = Math.max(1, Math.min(10, Math.round(this.analysisInputsByKey[key].durationSec / 60)));
    return mins;
  }

  onAnalysisDurationChange(conn: MountPointConnection, minutes: number): void {
    const key = this.getConnectionKey(conn);
    this.ensureAnalysisInputsFor(conn);
    const clamped = Math.max(1, Math.min(10, Number(minutes) || 5));
    this.analysisInputsByKey[key].durationSec = clamped * 60;
  }

  hasAnalysisResult(conn: MountPointConnection): boolean {
    return !!this.analysisResultsByMountpoint[conn.mountPoint || ''];
  }

  keyOrder = (a: KeyValue<string, any>, b: KeyValue<string, any>): number => 0;

  isObject(val: any): boolean { return val && typeof val === 'object' && !Array.isArray(val); }
  isArray(val: any): boolean { return Array.isArray(val); }
  isUrl(val: any): boolean { return typeof val === 'string' && /^(https?:)?\/\//i.test(val); }

  isExpanded(mp: string, path: string): boolean {
    return !!(this.analysisExpandedByMountpoint[mp] || this.analysisNodeExpanded[mp]?.[path]);
  }

  toggleNode(mp: string, path: string): void {
    if (!this.analysisNodeExpanded[mp]) this.analysisNodeExpanded[mp] = {};
    this.analysisNodeExpanded[mp][path] = !this.analysisNodeExpanded[mp][path];
  }

  expandAll(mp: string): void {
    this.analysisExpandedByMountpoint[mp] = true;
  }

  collapseAll(mp: string): void {
    this.analysisExpandedByMountpoint[mp] = false;
    this.analysisNodeExpanded[mp] = {};
  }

  copyJson(conn: MountPointConnection): void {
    const mp = conn.mountPoint || '';
    const data = this.analysisResultsByMountpoint[mp];
    if (!data) return;
    const text = JSON.stringify(data, null, 2);
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
      }
      this.dialog.open(AcknowledgeDialogComponent, { data: 'Analysis JSON copied to clipboard.' });
    } catch (e) {
      this.dialog.open(AcknowledgeDialogComponent, { data: 'Copy failed.' });
    }
  }

  onStartAnalysis(conn: MountPointConnection): void {
    this.ensureAnalysisInputsFor(conn);
    if (this.isDemo) {
      this.readOnlyNotice();
      return;
    }
    if (!this.perms.hasAny(['analyzer-pro'])) {
      this.dialog.open(AcknowledgeDialogComponent, { data: 'You do not have permission to start analyzer.' });
      return;
    }
    const mountpoint = conn.mountPoint;
    const userId = conn.userId;
    const serverHost = conn.server?.trim();
    const port = conn.port?.trim();
    if (!mountpoint || !userId || !serverHost || !port) {
      this.snackBar.open('Mount point connection is missing required fields.', 'Close', { duration: 3000 });
      return;
    }
    const overrides = {
      userId,
      server: `${serverHost}:${port}`,
      username: conn.downloaderUserName,
      password: conn.downloaderPassword
    };
    const key = mountpoint;
    this.analysisStatusByMountpoint[key] = 'Submitting analysis request...';
    this.analysisLoadingByMountpoint[key] = true;
    const durationSec = this.analysisInputsByKey[this.getConnectionKey(conn)]?.durationSec ?? 120;
    this.userService.startAnalysis(mountpoint, durationSec, overrides).subscribe({
      next: (res) => {
        this.analysisLoadingByMountpoint[key] = false;
        const body: any = (res as any)?.body ?? res;
        const msg = body?.message || (body?.accepted ? 'Analysis request accepted.' : 'Analyzer request processed.');
        this.analysisStatusByMountpoint[key] = msg;
        this.dialog.open(AcknowledgeDialogComponent, { data: msg });
      },
      error: (err) => {
        this.analysisLoadingByMountpoint[key] = false;
        const status = err?.status;
        const raw = err?.error?.error || err.message || 'Unknown Error';
        const friendly = status === 0
          ? 'Network error: backend unreachable or request aborted. Please try again.'
          : `Failed to start analysis: ${raw}`;
        this.analysisStatusByMountpoint[key] = friendly;
        this.dialog.open(AcknowledgeDialogComponent, { data: friendly });
      }
    });
  }

  onRefreshAnalysisResult(conn: MountPointConnection): void {
    this.refreshAnalysisResult(conn);
  }

  private refreshAnalysisResult(conn: MountPointConnection): void {
    const mountpoint = conn.mountPoint;
    if (!mountpoint) {
      return;
    }
    this.analysisStatusByMountpoint[mountpoint] = 'Fetching latest analysis report...';
    this.analysisFetchingByMountpoint[mountpoint] = true;
    this.userService.getAnalysisReport(mountpoint).subscribe({
      next: (out) => {
        this.analysisFetchingByMountpoint[mountpoint] = false;
        if (out) {
          this.analysisResultsByMountpoint[mountpoint] = out;
          this.analysisStatusByMountpoint[mountpoint] = 'Latest analysis report loaded.';
          this.expandAll(mountpoint);
          this.updateSummaryFromReport(mountpoint, out);
        } else {
          this.analysisResultsByMountpoint[mountpoint] = null;
          this.analysisStatusByMountpoint[mountpoint] = 'Analysis report not ready yet.';
          this.clearSummary(mountpoint);
        }
      },
      error: (err) => {
        this.analysisFetchingByMountpoint[mountpoint] = false;
        const raw = err?.error || err?.message;
        this.analysisStatusByMountpoint[mountpoint] = raw ? String(raw) : 'Failed to retrieve analysis report.';
        this.analysisResultsByMountpoint[mountpoint] = null;
        this.clearSummary(mountpoint);
      }
    });
  }

  private renderSummaryIfPresent(conn?: MountPointConnection | null): void {
    const mp = conn?.mountPoint;
    if (!mp) return;
    if (this.analyzerSummaryByMountpoint[mp]) {
      this.scheduleSummaryRender(mp);
    }
  }

  private clearSummary(mountpoint: string): void {
    if (!mountpoint) return;
    delete this.analyzerSummaryByMountpoint[mountpoint];
    delete this.generatedAtByMountpoint[mountpoint];
    delete this.coordsByMountpoint[mountpoint];
    const inst = this.mapInstances[mountpoint];
    if (inst && typeof inst.remove === 'function') {
      try { inst.remove(); } catch { /* ignore */ }
    }
    delete this.mapInstances[mountpoint];
  }

  private updateSummaryFromReport(mountpoint: string, json: any): void {
    if (!mountpoint || !json) {
      this.clearSummary(mountpoint);
      return;
    }
    const pick = (k: string) => this.findValueByKeyCaseInsensitive(json, k);
    const summary: { [k: string]: any } = {
      protocol: pick('protocol'),
      location: pick('location'),
      geodetic_height_meters: pick('geodetic_height_meters'),
      orthometric_height_meters: pick('orthometric_height_meters'),
      coordinate_system: pick('coordinate_system'),
      Average: pick('Average'),
      Average_top_50: pick('Average_top_50'),
      signal_quality: pick('signal_quality'),
      generated_at: pick('generated_at')
    };
    const meaningful = Object.values(summary).some(v => v !== undefined && v !== null && String(v).trim() !== '');
    if (!meaningful) {
      this.clearSummary(mountpoint);
      return;
    }
    this.analyzerSummaryByMountpoint[mountpoint] = summary;
    if (summary['generated_at']) {
      this.generatedAtByMountpoint[mountpoint] = String(summary['generated_at']);
    }
    this.scheduleSummaryRender(mountpoint);
  }

  private scheduleSummaryRender(mountpoint: string): void {
    if (!mountpoint) return;
    const summary = this.analyzerSummaryByMountpoint[mountpoint];
    if (!summary) return;
    const mapId = this.getMapElementId(mountpoint);
    const snrId = this.getSnrSvgId(mountpoint);
    const tryRender = (attempt: number) => {
      let mapReady = !this.showMap || this.isMapCollapsed(mountpoint) || !summary.location;
        const grade = summary?.signal_quality?.grade;
        let snrReady = !this.showSnr || this.isSnrCollapsed(mountpoint) || ((grade == null || String(grade).trim() === '') && summary.Average === undefined);
      if (!mapReady) {
        const mapElem = document.getElementById(mapId);
        if (mapElem) {
          this.renderMapForMountpoint(mountpoint, summary.location);
          mapReady = true;
        }
      }
      if (!snrReady) {
        const snrElem = document.getElementById(snrId);
        if (snrElem) {
            this.renderSignalQualityChart(mountpoint, summary.signal_quality, summary.Average);
          snrReady = true;
        }
      }
      if ((mapReady && snrReady) || attempt > 120) {
        return;
      }
      requestAnimationFrame(() => tryRender(attempt + 1));
    };
    requestAnimationFrame(() => tryRender(0));
  }

  private safeDomId(value: string): string {
    return value.replace(/[^a-zA-Z0-9_-]/g, '_');
  }

  getSnrSvgId(mountpoint: string): string {
    return `snrsvg_pro_${this.safeDomId(mountpoint)}`;
  }

  getMapElementId(mountpoint: string): string {
    return `map_pro_${this.safeDomId(mountpoint)}`;
  }

  private findValueByKeyCaseInsensitive(data: any, key: string): any {
    if (data == null) return undefined;
    if (typeof data !== 'object') return undefined;
    const lowerKey = key.toLowerCase();
    if (Array.isArray(data)) {
      for (const item of data) {
        const v = this.findValueByKeyCaseInsensitive(item, key);
        if (v !== undefined) return v;
      }
      return undefined;
    }
    for (const k of Object.keys(data)) {
      if (k.toLowerCase() === lowerKey) return data[k];
      const v = this.findValueByKeyCaseInsensitive(data[k], key);
      if (v !== undefined) return v;
    }
    return undefined;
  }

  hasAnalyzerSummary(mp: string | undefined): boolean {
    if (!mp) return false;
    const s = this.analyzerSummaryByMountpoint[mp];
    if (!s || typeof s !== 'object') return false;
    const loc = s['location'];
    const avg = s['Average'];
    const gh = s['geodetic_height_meters'];
    const oh = s['orthometric_height_meters'];
    return (loc && String(loc).trim().length > 0) || (avg !== undefined) || (gh !== undefined) || (oh !== undefined);
  }

  isSnrCollapsed(mp: string): boolean {
    return !!this.snrCollapsedByMountpoint[mp];
  }

  toggleSnrSection(mp: string): void {
    const current = this.isSnrCollapsed(mp);
    const next = !current;
    this.snrCollapsedByMountpoint[mp] = next;
    if (!next) {
      this.scheduleSummaryRender(mp);
    }
  }

  isMapCollapsed(mp: string): boolean {
    return !!this.mapCollapsedByMountpoint[mp];
  }

  toggleMapSection(mp: string): void {
    const current = this.isMapCollapsed(mp);
    const next = !current;
    this.mapCollapsedByMountpoint[mp] = next;
    if (!next) {
      this.scheduleSummaryRender(mp);
    }
  }

  getAveragePercent(value: any): number {
    const n = Number(value);
    if (!isFinite(n)) return 0;
    const pct = (n / 63) * 100;
    return Math.max(0, Math.min(100, pct));
  }

  private renderSignalQualityChart(mountpoint: string, signalQuality: any, averageFallback: any): void {
    const svgId = this.getSnrSvgId(mountpoint);
    const svg = document.getElementById(svgId) as SVGSVGElement | null;
    if (!svg) return;
    try {
      while (svg.firstChild) svg.removeChild(svg.firstChild);
    } catch {}
    const width = (svg.parentElement?.clientWidth ?? svg.clientWidth ?? 800);
    const height = svg.clientHeight || 220;
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    const labels = this.signalQualityCategories;

    // Preferred: use backend grade (CN0 table-driven).
    // Fallback: derive from legacy average (SNR).
    let idx = -1;
    const grade = signalQuality?.grade ? String(signalQuality.grade) : '';
    if (grade) {
      idx = labels.findIndex(l => l.toLowerCase() === grade.toLowerCase());
    }
    if (idx < 0) {
      const avg = Number(averageFallback);
      if (isFinite(avg)) {
        const cutoffs = [15, 25, 35, 45, 55, 63];
        idx = cutoffs.length - 1;
        for (let i = 0; i < cutoffs.length; i++) {
          if (avg <= cutoffs[i]) { idx = i; break; }
        }
      } else {
        idx = 0;
      }
    }
    const barWidth = width / labels.length;
    const baseline = height - 40;
    labels.forEach((label, i) => {
      const x = i * barWidth + 12;
      const barHeight = 40 + (i / (labels.length - 1)) * 120;
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', String(x));
      rect.setAttribute('y', String(baseline - barHeight));
      rect.setAttribute('width', String(barWidth - 24));
      rect.setAttribute('height', String(barHeight));
      rect.setAttribute('rx', '6');
      rect.setAttribute('ry', '6');
      rect.setAttribute('fill', i === idx ? '#0d6efd' : '#d1d5db');
      svg.appendChild(rect);

      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', String(x + (barWidth - 24) / 2));
      text.setAttribute('y', String(baseline + 18));
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('font-size', '12');
      text.textContent = label;
      svg.appendChild(text);
    });
  }

  private readOnlyNotice(): void {
    this.dialog.open(AcknowledgeDialogComponent, { data: 'You have read-only access, but not permission to make changes.' });
  }

  private parseLatLngFromLocation(loc: string | undefined): { lat: number; lng: number } | null {
    if (!loc || typeof loc !== 'string') return null;
    const match = loc.match(/([\d.+-]+)°?\s*([NnSs])\s*,\s*([\d.+-]+)°?\s*([EeWw])/);
    if (!match) return null;
    let lat = parseFloat(match[1]);
    const ns = match[2].toUpperCase();
    let lng = parseFloat(match[3]);
    const ew = match[4].toUpperCase();
    if (ns === 'S') lat = -lat;
    if (ew === 'W') lng = -lng;
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    return null;
  }

  private renderMapForMountpoint(mountpoint: string, locationStr?: string): void {
    const coords = this.parseLatLngFromLocation(locationStr || '');
    const elemId = this.getMapElementId(mountpoint);
    const elem = document.getElementById(elemId);
    if (!elem || !coords) return;
    const Lref = (window as any).L;
    if (!Lref) return;
    const existing = this.mapInstances[mountpoint];
    if (existing && typeof existing.remove === 'function') {
      try { existing.remove(); } catch {}
    }
    try { (elem as any)._leaflet_id = undefined; } catch {}
    elem.innerHTML = '';
    const map = Lref.map(elemId, {
      zoomControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      touchZoom: false,
      inertia: false,
      zoomAnimation: false,
      fadeAnimation: false
    });
    const street = Lref.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 14,
      attribution: '© OpenStreetMap'
    });
    street.addTo(map);
    Lref.marker([coords.lat, coords.lng]).addTo(map);
    map.setView([coords.lat, coords.lng], 15);
    this.coordsByMountpoint[mountpoint] = { lat: coords.lat, lng: coords.lng };
    this.mapInstances[mountpoint] = map;
    setTimeout(() => map.invalidateSize(), 0);
  }

  private outOfChina(lat: number, lng: number): boolean {
    return lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271;
  }

  private transformLat(x: number, y: number): number {
    let ret = -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x));
    ret += (20.0 * Math.sin(6.0 * x * Math.PI) + 20.0 * Math.sin(2.0 * x * Math.PI)) * 2.0 / 3.0;
    ret += (20.0 * Math.sin(y * Math.PI) + 40.0 * Math.sin(y / 3.0 * Math.PI)) * 2.0 / 3.0;
    ret += (160.0 * Math.sin(y / 12.0 * Math.PI) + 320 * Math.sin(y * Math.PI / 30.0)) * 2.0 / 3.0;
    return ret;
  }

  private transformLng(x: number, y: number): number {
    let ret = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x));
    ret += (20.0 * Math.sin(6.0 * x * Math.PI) + 20.0 * Math.sin(2.0 * x * Math.PI)) * 2.0 / 3.0;
    ret += (20.0 * Math.sin(x * Math.PI) + 40.0 * Math.sin(x / 3.0 * Math.PI)) * 2.0 / 3.0;
    ret += (150.0 * Math.sin(x / 12.0 * Math.PI) + 300.0 * Math.sin(x / 30.0 * Math.PI)) * 2.0 / 3.0;
    return ret;
  }

  private wgs84ToGcj02(lat: number, lng: number): { lat: number; lng: number } {
    if (this.outOfChina(lat, lng)) return { lat, lng };
    const a = 6378245.0;
    const ee = 0.00669342162296594323;
    let dLat = this.transformLat(lng - 105.0, lat - 35.0);
    let dLng = this.transformLng(lng - 105.0, lat - 35.0);
    const radLat = lat / 180.0 * Math.PI;
    let magic = Math.sin(radLat);
    magic = 1 - ee * magic * magic;
    const sqrtMagic = Math.sqrt(magic);
    dLat = (dLat * 180.0) / ((a * (1 - ee)) / (magic * sqrtMagic) * Math.PI);
    dLng = (dLng * 180.0) / (a / sqrtMagic * Math.cos(radLat) * Math.PI);
    return { lat: lat + dLat, lng: lng + dLng };
  }

  private gcj02ToBd09(lat: number, lng: number): { lat: number; lng: number } {
    const x_pi = Math.PI * 3000.0 / 180.0;
    const z = Math.sqrt(lng * lng + lat * lat) + 0.00002 * Math.sin(lat * x_pi);
    const theta = Math.atan2(lat, lng) + 0.000003 * Math.cos(lng * x_pi);
    return { lng: z * Math.cos(theta) + 0.0065, lat: z * Math.sin(theta) + 0.006 };
  }

  getGoogleMapUrl(mp: string, mode: 'sat' | 'map'): string {
    const c = this.coordsByMountpoint[mp];
    if (!c) return '#';
    const t = mode === 'sat' ? 'k' : 'm';
    return `https://www.google.com/maps?q=${c.lat},${c.lng}&z=18&t=${t}`;
  }

  getBingMapUrl(mp: string, mode: 'sat' | 'map'): string {
    const c = this.coordsByMountpoint[mp];
    if (!c) return '#';
    const style = mode === 'sat' ? 'a' : 'r';
    const label = encodeURIComponent('Location');
    return `https://www.bing.com/maps?cp=${c.lat}~${c.lng}&lvl=18&style=${style}&sp=point.${c.lat}_${c.lng}_${label}`;
  }

  getAmapUrl(mp: string, mode: 'sat' | 'map'): string {
    const c = this.coordsByMountpoint[mp];
    if (!c) return '#';
    const g = this.wgs84ToGcj02(c.lat, c.lng);
    return `https://uri.amap.com/marker?position=${g.lng},${g.lat}&name=Location&callnative=0`;
  }

  getBaiduUrl(mp: string, mode: 'sat' | 'map'): string {
    const c = this.coordsByMountpoint[mp];
    if (!c) return '#';
    const g = this.wgs84ToGcj02(c.lat, c.lng);
    const b = this.gcj02ToBd09(g.lat, g.lng);
    return `https://api.map.baidu.com/marker?location=${b.lat},${b.lng}&title=Location&content=&output=html&zoom=18&coord_type=bd09ll`;
  }

  getConnectionKey(conn?: MountPointConnection | null): string {
    if (!conn) return '';
    if (conn.mountPointId != null) {
      return `id:${conn.mountPointId}`;
    }
    return `mp:${conn.mountPoint || ''}`;
  }
}


